import { motion, AnimatePresence } from 'framer-motion'
import { Card } from '../../components/Card'
import { Skeleton } from '../../components/Skeleton'
import { cx } from '../../lib/cx'
import type { MealDay } from './types'
import { listYmd, ymdToLabel } from './useMeals'

export function MealCards(props: {
  baseDateISO: string
  data?: Record<string, MealDay>
  isLoading: boolean
  error?: unknown
  showAllergies: boolean
}) {
  const { baseDateISO, data, isLoading, error, showAllergies } = props
  const ymds = listYmd(baseDateISO)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {ymds.map((ymd) => (
          <Card key={ymd} className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-4 w-10" />
              </div>
              <Skeleton className="h-7 w-16" rounded="full" />
            </div>
            <div className="mt-4 space-y-3">
              <Skeleton className="h-4 w-[78%]" />
              <Skeleton className="h-4 w-[64%]" />
              <Skeleton className="h-4 w-[70%]" />
            </div>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    const message = error instanceof Error ? error.message : '알 수 없는 오류'
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="p-5">
          <div className="text-[15px] font-semibold">불러오지 못했습니다</div>
          <div className="mt-1 text-[14px] text-muted break-all">{message}</div>
          <div className="mt-4 text-[14px] text-muted">
            NEIS(Open API) 응답 또는 CORS 설정에 따라 브라우저에서 차단될 수 있습니다.
          </div>
        </Card>
      </motion.div>
    )
  }

  return (
    <motion.div
      layout
      className="flex flex-col gap-3"
      initial="hidden"
      animate="visible"
      variants={{
        visible: { transition: { staggerChildren: 0.1 } }
      }}
    >
      {ymds.map((ymd) => {
        const d = data?.[ymd]
        const label = ymdToLabel(ymd)
        const hasItems = (d?.items?.length ?? 0) > 0

        return (
          <motion.div
            key={ymd}
            layout
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
          >
            <Card className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <div className="text-[18px] font-extrabold tracking-[-0.02em]">
                      {label.mmdd}
                    </div>
                    <div className="text-[14px] font-semibold text-muted">({label.dow})</div>
                  </div>
                  <div className="mt-1 text-[13px] font-semibold text-muted">중식</div>
                </div>
                <div
                  className={cx(
                    'shrink-0 rounded-full px-3 py-1 text-[12px] font-semibold transition-colors duration-300',
                    hasItems
                      ? 'bg-success/10 text-success'
                      : 'bg-muted/10 text-muted',
                  )}
                >
                  {hasItems ? '제공' : '없음'}
                </div>
              </div>

              <div className="mt-4">
                {!hasItems ? (
                  <div className="text-[14px] text-muted">급식 정보가 없습니다.</div>
                ) : (
                  <ul className="space-y-2">
                    {d!.items.map((item, idx) => (
                      <motion.li
                        key={idx}
                        layout
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex items-start gap-2"
                      >
                        <span className="mt-[6px] h-[5px] w-[5px] rounded-full bg-primary" />
                        <div className="min-w-0">
                          <div className="text-[15px] leading-6 font-semibold">
                            {item.name}
                          </div>
                          <AnimatePresence>
                            {showAllergies && item.allergies.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="text-[12px] text-muted mt-0.5 overflow-hidden"
                              >
                                알레르기: {item.allergies.join(', ')}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          </motion.div>
        )
      })}
    </motion.div>
  )
}

